import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { RootState } from '../../store/rootReducer'

import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import usePageLoaded from '../../hooks/usePageLoaded'
import Title from '../../components/ui/title/title'
import CreateNewTemporaryPost from './ui/newPostForm/CreateNewTemporaryPost'

import Popup from '../../components/ui/popup'
import PostConstructor from './ui/postConstructor/PostConstructor'
import {
    clearEphemeralList,
    clearPost,
    fetchEphemeralList,
    fetchEphemeralPost
} from '../../store/ephemeral/ephemeralSlice'

import styles from './TemporaryPhoto.module.sass'
import PaginationLoader from '../../components/ui/paginationLoader/PaginationLoader'
import { useTranslation } from 'react-i18next'
import { createLocalizedPath } from '../../utils/localizationHelpers'
import { META } from '../../constants/meta'
import { getLangFromPath } from '../../i18next'
import LikeList from './ui/likeList/LikeList'
import ScrollToTop from './ui/scrollToTop/ScrollToTop'

const TemporaryPhotos = () => {
    const [t] = useTranslation()

    usePageLoaded()
    const history = useHistory()
    const location = useLocation()
    const dispatch = useDispatch()

    const list = useSelector((state: RootState) => state.ephemeral.list)
    const additionalPosts = useSelector((state: RootState) => state.ephemeral.additionalPosts)
    const isLoadingList = useSelector((state: RootState) => state.ephemeral.isLoadingList)
    const post = useSelector((state: RootState) => state.ephemeral.post)
    const isLoadingPost = useSelector((state: RootState) => state.ephemeral.isLoadingPost)
    const hasMorePosts = useSelector((state: RootState) => state.ephemeral.hasMorePosts)
    const loggedIn = useSelector((state: RootState) => state.member.loggedIn)

    const [isFullPostOpen, setIsFullPostOpen] = useState(false)
    const [replyCommentId, setReplyCommentId] = useState<string | null>(null)
    const [fullList, setFullList] = useState<any[]>([])
    const [updateCounter, setUpdateCounter] = useState(0)

    const handleUpdateList = useCallback(() => {
        setUpdateCounter((prev) => prev + 1)
    }, [updateCounter])

    const lastElement = useRef<HTMLDivElement>(null)
    const observer = useRef<IntersectionObserver | null>(null)

    useEffect(() => {
        dispatch(clearEphemeralList())
        dispatch(fetchEphemeralList({ offset: 0 }))
    }, [loggedIn, updateCounter])

    useEffect(() => {
        const map = new Map()
        ;[...additionalPosts, ...list].forEach((post) => map.set(post.id, post))
        setFullList(Array.from(map.values()))
    }, [list.length, additionalPosts])

    useEffect(() => {
        const id = new URLSearchParams(location.search).get('post')
        if (id) {
            setIsFullPostOpen(true)
            dispatch(fetchEphemeralPost(id))
        } else {
            setIsFullPostOpen(false)
            dispatch(clearPost())
        }
    }, [location.search, loggedIn])

    useEffect(() => {
        const update = new URLSearchParams(location.search).get('update')
        if (update == '1') {
            setIsFullPostOpen(false)
            setUpdateCounter((prev) => prev + 1)
            history.push(createLocalizedPath(`/ephemeral/`))
        }
    }, [location.search])

    const loadNextPosts = () => {
        dispatch(fetchEphemeralList({ offset: list.length }))
    }

    useEffect(() => {
        if (window && 'IntersectionObserver' in window && lastElement.current) {
            // @ts-ignore
            const callback = function (entries) {
                if (entries[0].isIntersecting && list.length > 0 && hasMorePosts && !isLoadingPost) {
                    loadNextPosts()
                }
            }
            observer.current = new IntersectionObserver(callback)
            observer.current.observe(lastElement.current)
        }
        return () => {
            if (observer.current) {
                observer.current.disconnect()
            }
        }
    }, [list.length])

    const [isLikeListOpen, setIsLikeListOpen] = useState(false)

    useEffect(() => {
        const id = new URLSearchParams(location.search).get('likes')
        if (id) {
            setIsLikeListOpen(true)
        } else {
            setIsLikeListOpen(false)
        }
    }, [location.search, loggedIn])

    return (
        <div className={`pageContent ${styles['pageContent']}`}>
            <Helmet>
                <title>{META.ephemeral[getLangFromPath(location.pathname)].title}</title>
                <meta name="description" content={META.ephemeral[getLangFromPath(location.pathname)].description} />
            </Helmet>
            <Title text={t('Ephemeral stories 24 hours')} />
            {!isFullPostOpen && <ScrollToTop />}

            <div className={styles['photoCard']}>
                <CreateNewTemporaryPost handleUpdateList={handleUpdateList} />
                <div className={styles['commentSection']}>
                    <div className={styles['commentsList']}>
                        {fullList.map((p) => (
                            <PostConstructor
                                key={p.id}
                                post={p}
                                setIsFullPostOpen={setIsFullPostOpen}
                                replyCommentId={replyCommentId}
                                setReplyCommentId={setReplyCommentId}
                                setIsLikeListOpen={setIsLikeListOpen}
                            />
                        ))}
                    </div>
                </div>

                {hasMorePosts && (
                    <div className={styles['loadMore']} ref={lastElement}>
                        {!isLoadingList && <span onClick={loadNextPosts}>{t('Load more')}</span>}
                        {isLoadingList && <PaginationLoader />}
                    </div>
                )}
            </div>

            {isFullPostOpen && (
                <Popup
                    onClose={() => {
                        setIsFullPostOpen(false)
                        history.replace(location.pathname)
                    }}
                    classname={styles['postPopup']}
                >
                    <PostConstructor
                        isInPopup
                        post={post}
                        setIsFullPostOpen={setIsFullPostOpen}
                        replyCommentId={replyCommentId}
                        setReplyCommentId={setReplyCommentId}
                        setIsLikeListOpen={setIsLikeListOpen}
                    />
                </Popup>
            )}
            {isLikeListOpen && (
                <LikeList
                    onClose={() => {
                        if (location.search.includes('?post')) {
                            history.push(location.pathname + '?post=' + post.id)
                        } else {
                            history.push(createLocalizedPath(`/ephemeral/`))
                        }
                        setIsLikeListOpen(false)
                    }}
                />
            )}
        </div>
    )
}

// @ts-ignore
TemporaryPhotos.getInitialProps = async ({ store, match }) => {
    try {
        const {
            ephemeral: { list }
        } = store.getState()

        if (!list || !list.length) {
            await store.dispatch(fetchEphemeralList({ offset: 0 }))

            const updatedState = store.getState()
            if (!updatedState.ephemeral.list || !updatedState.ephemeral.list.length) {
                return { statusCode: 404 }
            }
        }

        return {}
    } catch (e) {
        return { statusCode: 404 }
    }
}

export default TemporaryPhotos
